import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Package, Plus } from 'lucide-react';
import { useOrganization } from '../contexts/OrganizationContext';
import { usePermissions } from '../hooks/usePermissions';
import { useUser } from '../contexts/UserContext';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { useInfiniteList, type InfiniteList } from '../hooks/useInfiniteList';
import { useResource } from '../hooks/useResource';
import { apiService } from '../services/api';
import { TemplateProcessor, TemplateProcessorVersion } from '../types/api';
import { ColumnPicker } from '../components/ColumnPicker';
import { DataTable, DataTableColumn, SearchInput, SortState, hasListError, isListRetrying, listTableProps } from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';
import { ListEmptyState } from '../components/ListEmptyState';
import { ErrorState } from '../components/ErrorState';
import { PublishTemplateWizard, type PublishWizardMode } from '../components/PublishTemplateWizard';
import { StatusBadge } from '../components/StatusBadge';
import { TemplateWorkbenchDialog } from '../components/TemplateWorkbenchDialog';
import { Button } from '../components/ui/button';
import { ListFilter } from '../components/ListFilter';
import { TEMPLATE_FILTER_OPTIONS, type TemplateStatusFilter } from '../lib/templateStatus';
import { type InMemoryListConfig } from '../lib/listPage';
import { EMPTY_VALUE } from '../lib/formatters';
import { DateCell } from '../components/Timestamp';
import { formatSensorTypes } from '../lib/labels';
import { routeCopy } from '../routes';

const { title: PAGE_TITLE, description: PAGE_DESCRIPTION } = routeCopy('/my-processor-templates');

/** Column-visibility preferences are stored per table id. */
const TABLE_ID = 'my-processor-templates';

/**
 * Picks the best display value for a template version label.
 */
const getDisplayVersion = (version: Pick<TemplateProcessorVersion, 'version' | 'tag'>): string => {
  return version.version || version.tag || EMPTY_VALUE;
};

/** The raw label a version sorts by — absent so NULLS LAST can pin it. */
const versionSortValue = (
  version: Pick<TemplateProcessorVersion, 'version' | 'tag'> | null,
): string | undefined => version?.version || version?.tag || undefined;

type TemplateSortKey =
  | 'name'
  | 'slug'
  | 'versionStatus'
  | 'currentVersion'
  | 'currentVersionPushedAt'
  | 'latestVersion'
  | 'latestVersionPushedAt'
  | 'hasCredentials'
  | 'createdAt'
  | 'updatedAt';

const SORTABLE_KEYS: ReadonlyArray<TemplateSortKey> = [
  'name',
  'slug',
  'versionStatus',
  'currentVersion',
  'currentVersionPushedAt',
  'latestVersion',
  'latestVersionPushedAt',
  'hasCredentials',
  'createdAt',
  'updatedAt',
];

const DEFAULT_SORT: SortState = { column: 'name', direction: 'asc' };

/** `SortState` carries a bare string; narrow it back to the whitelist. */
const toTemplateSort = (
  sort: SortState,
): { column: TemplateSortKey; direction: 'asc' | 'desc' } =>
  SORTABLE_KEYS.includes(sort.column as TemplateSortKey)
    ? { column: sort.column as TemplateSortKey, direction: sort.direction }
    : { column: 'name', direction: sort.direction };

/**
 * Publishing state, derived rather than stored: the API returns the selected
 * version and the newest pushed one, and the interesting fact is whether they
 * are the same image. Digests are the identity that matters; the version label
 * is only a fallback for a row whose digest never made it into the response.
 */
type VersionStatus =
  | 'draft'
  | 'in_review'
  | 'rejected'
  | 'revoked'
  | 'approved'
  | 'update_available'
  | 'live';

const versionStatusOf = (template: TemplateProcessor): VersionStatus => {
  const selected = template.selectedVersion;
  const latest = template.latestVersion;

  // The review state of the NEWEST push outranks everything else, because it is
  // the only thing the publisher can act on. A template can be live and in
  // review at the same time — an approved version serving traffic while a newer
  // one waits — and in that case "In review" is the fact worth surfacing;
  // whether it is also live is answered by the tab the row is sitting under.
  if (latest?.reviewStatus === 'pending') return 'in_review';
  if (latest?.reviewStatus === 'rejected') return 'rejected';

  if (!selected) return 'draft';
  // "Live" means the orchestrator runs it — `isActive` — not merely that it is
  // the selected pointer. Revoking clears isActive but leaves the pointer, and
  // a revoked version badged green would be a false signal about code running
  // against other organizations' sensors.
  if (!selected.isActive) {
    return selected.reviewStatus === 'pending' ? 'in_review' : selected.reviewStatus;
  }
  if (!latest) return 'live';

  const behind =
    selected.digest && latest.digest
      ? selected.digest !== latest.digest
      : getDisplayVersion(selected) !== getDisplayVersion(latest);

  return behind ? 'update_available' : 'live';
};

/** Sort order for the status column: least published first. */
const VERSION_STATUS_RANK: Record<VersionStatus, number> = {
  draft: 0,
  rejected: 1,
  revoked: 2,
  in_review: 3,
  approved: 4,
  update_available: 5,
  live: 6,
};

/** Which slice of the publisher's own templates the filter is showing. */
type TemplateFilter = TemplateStatusFilter;

/**
 * Client-side, unlike the system-admin panel's server-side filter, because
 * `GET /template-processors` takes no list parameters and this page already
 * pages and sorts the whole array in memory.
 */
const matchesTemplateFilter = (template: TemplateProcessor, filter: TemplateFilter): boolean => {
  if (filter === 'all') return true;

  const inReview = template.latestVersion?.reviewStatus === 'pending';
  const live = Boolean(template.selectedVersion?.isActive);

  if (filter === 'pending') return inReview;
  if (filter === 'live') return live;
  // Draft is the COMPLEMENT of the other two — nothing waiting on a reviewer,
  // nothing running. Defined this way it cannot overlap them, and no template
  // can fall outside every filter: a template with no version pushed yet used
  // to match neither 'pending' nor 'live' and was reachable only under All.
  return !live && !inReview;
};

/**
 * GET /template-processors takes no list parameters and returns the whole
 * array, so search / sort / pagination happen here. Several of the columns
 * below have no column behind them to ORDER BY anyway — publishing status and
 * credential state are both derived after the query.
 */
const TEMPLATE_LIST_CONFIG: InMemoryListConfig<TemplateProcessor, TemplateSortKey> = {
  searchFields: (template) => [template.name, template.slug, template.description],
  sortAccessors: {
    name: (template) => template.name,
    slug: (template) => template.slug,
    versionStatus: (template) => VERSION_STATUS_RANK[versionStatusOf(template)],
    currentVersion: (template) => versionSortValue(template.selectedVersion),
    currentVersionPushedAt: (template) =>
      template.selectedVersion ? Date.parse(template.selectedVersion.createdAt) : undefined,
    latestVersion: (template) => versionSortValue(template.latestVersion),
    latestVersionPushedAt: (template) =>
      template.latestVersion ? Date.parse(template.latestVersion.createdAt) : undefined,
    hasCredentials: (template) => template.hasCredentials,
    createdAt: (template) => Date.parse(template.createdAt),
    updatedAt: (template) => Date.parse(template.updatedAt),
  },
  tiebreak: (template) => template.id,
};

const MyProcessorTemplates: React.FC = () => {
  const { accessToken } = useUser();
  const {
    selectedOrganization,
    isLoading: orgLoading,
  } = useOrganization();
  const selectedOrganizationId = selectedOrganization?.id;
  const orgId = selectedOrganization?.id ?? null;

  const { hasPermission } = usePermissions();
  const mayReadCredentials =
    hasPermission('app_clients:write') && hasPermission('processor_template_versions:write');
  /**
   * Submitting a version carries its lens routing in the same request, and the
   * API refuses a real routing change without this. Passed in so the wizard can
   * say so up front rather than letting the user tick boxes and meet a 403.
   */
  const mayWriteLenses = hasPermission('lenses:write');
  const mayMakeLensPublic = hasPermission('lenses:make_public');
  /**
   * The one permission every write in the publish flow enforces.
   *
   * It gates two things that look unrelated and are not: provisioning a missing
   * publisher credential (POST .../credentials mints one rather than reading
   * one) and registering a version (POST .../versions). Both are the same
   * grant, so they share the same derived flag rather than a second
   * `hasPermission` call that could drift from it.
   */
  const mayWriteVersions = hasPermission('processor_template_versions:write');
  /**
   * What GET /template-processors/{slug}/versions requires, and a genuinely
   * separate grant from the write above — some read-only members have it and
   * some do not. Without it the workbench has no Versions section at all.
   */
  const mayReadVersions = hasPermission('processor_template_versions:read');
  const mayWriteTemplates = hasPermission('processor_templates:write');

  /**
   * The COMPLETE array the endpoint returned, kept alongside the query so the
   * optimistic edit handlers work on every template — not just the page
   * currently on screen.
   */
  const [allTemplates, setAllTemplates] = useState<TemplateProcessor[]>([]);
  /** The list's row actions, for handlers declared before the list itself. */
  const listRef = useRef<Pick<InfiniteList<TemplateProcessor>, 'prependItem' | 'patchItem'> | null>(null);

  /**
   * The one dialog a row click opens: read and edit, info and versions,
   * credentials folded in. It replaced four separate dialogs (details, edit,
   * credentials, versions) and the four row icons that reached them.
   */
  const [workbenchTarget, setWorkbenchTarget] = useState<TemplateProcessor | null>(null);
  const [isWorkbenchOpen, setIsWorkbenchOpen] = useState(false);
  /**
   * Bumped when a write this page can see may have changed a template's version
   * history — which in practice means the wizard registering one. The workbench
   * fetches its own history and cannot observe that write, so this is how it is
   * told to ask again.
   */
  const [versionsToken, setVersionsToken] = useState(0);

  /**
   * Guided publish flow. `wizardTemplate` null means "start from creation";
   * a template means "resume wherever this one actually is".
   */
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardTemplate, setWizardTemplate] = useState<TemplateProcessor | null>(null);
  /**
   * Which flow the dialog is running, decided once at open time.
   *
   * Frozen for the dialog's lifetime on purpose: `wizardTemplate` is replaced
   * as the wizard writes (it calls back with the refreshed template), so a mode
   * derived on every render would flip from `setup` to `new-version` the
   * instant the first version registered — reshaping the step list, and the
   * rail, out from under the user mid-flow.
   */
  const [wizardMode, setWizardMode] = useState<PublishWizardMode>('setup');

  /**
   * Clears cached data when auth or org scope changes.
   */
  useEffect(() => {
    setAllTemplates([]);
    setIsWorkbenchOpen(false);
    setWorkbenchTarget(null);
    // The wizard holds a template and locally-acknowledged progress; both are
    // scoped to one tenant, so neither may survive an org switch.
    setIsWizardOpen(false);
    setWizardTemplate(null);
    setWizardMode('setup');
  }, [accessToken, selectedOrganizationId]);

  /**
   * Opens the workbench on one row.
   *
   * The ONLY way in, and the same way in for everybody: a member holding only
   * `processor_templates:read` gets the same dialog with values where the
   * inputs would be and no save bar, rather than a separate read-only surface
   * that would drift from this one.
   */
  const openWorkbench = (template: TemplateProcessor) => {
    setWorkbenchTarget(template);
    setIsWorkbenchOpen(true);
  };

  /**
   * Opens the guided publish flow. Pass a template to resume it, or null to
   * start from creation.
   */
  const openWizard = (target: TemplateProcessor | null) => {
    /*
     * The workbench, if it is open, STAYS open underneath.
     *
     * Deploying a version is a task with a beginning and an end, and when it
     * ends the destination is the dialog the user came from — now with one more
     * version in its list. Closing that dialog first would answer "do a task
     * and put me back" by putting them back on the table.
     */
    setWizardTemplate(target);
    /*
     * `latestVersion`, not `processorTemplateVersionId`.
     *
     * The API builds `latestVersion` from a LEFT JOIN LATERAL over
     * processor_template_versions with NO is_active predicate, so it answers
     * "has this template ever shipped an image". `processorTemplateVersionId`
     * is the id of the ACTIVE version, and is null for a template whose
     * versions were all registered inactive — that template has been through
     * setup, but reading the wrong field would send it round the full
     * onboarding again, including the scaffold step that overwrites its source.
     */
    setWizardMode(target?.latestVersion ? 'new-version' : 'setup');
    setIsWizardOpen(true);
  };

  /**
   * Folds a template either dialog created or changed back into the list.
   *
   * Both writing surfaces hand back the server's copy of the template, so the
   * cheapest correct thing is to upsert it into the array the table renders
   * from and into whichever dialog is holding it.
   */
  const handleTemplateChanged = useCallback((updated: TemplateProcessor) => {
    setWizardTemplate(updated);
    setWorkbenchTarget((prev) => (prev && prev.id === updated.id ? updated : prev));
    setAllTemplates((prev) => {
      const exists = prev.some((item) => item.id === updated.id);
      // The row itself: a new template is pinned first marked New, an existing
      // one patched where it is marked Updated (ruling 20). The array update
      // keeps the filter counts true.
      if (exists) listRef.current?.patchItem(updated.id, () => updated, { mark: 'updated' });
      else listRef.current?.prependItem(updated, { mark: 'new' });
      return exists ? prev.map((item) => (item.id === updated.id ? updated : item)) : [...prev, updated];
    });
  }, []);

  /**
   * The wizard's version of the same, plus the one thing only it causes.
   *
   * Registering a version is the ONLY write on this page that lengthens a
   * template's history, and the workbench underneath cannot see it happen — so
   * this is where the token moves. A rename or a "set current" goes through
   * `handleTemplateChanged` alone and costs no second read of a list that
   * cannot have changed.
   */
  const handleWizardTemplateChanged = useCallback((updated: TemplateProcessor) => {
    handleTemplateChanged(updated);
    setVersionsToken((token) => token + 1);
  }, [handleTemplateChanged]);

  const columns: Array<DataTableColumn<TemplateProcessor>> = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      className: 'max-w-[240px] truncate font-medium',
      render: (template) => template.name,
    },
    {
      key: 'slug',
      header: 'Slug',
      label: 'Slug',
      hideable: true,
      hideBelow: 'md',
      sortable: true,
      className: 'truncate font-mono text-xs text-muted-foreground',
      render: (template) => template.slug,
    },
    {
      key: 'versionStatus',
      header: 'Status',
      label: 'Status',
      hideable: true,
      sortable: true,
      render: (template) => {
        const status = versionStatusOf(template);
        // No label override: every state reads its registry word. The
        // registry key for 'in_review' is pending_review, shared with the
        // admin panel so both read "In review".
        return <StatusBadge status={status === 'in_review' ? 'pending_review' : status} />;
      },
    },
    {
      key: 'currentVersion',
      header: 'Current version',
      label: 'Current version',
      hideable: true,
      sortable: true,
      className: 'font-mono text-xs',
      render: (template) =>
        template.selectedVersion ? getDisplayVersion(template.selectedVersion) : EMPTY_VALUE,
    },
    {
      key: 'currentVersionPushedAt',
      header: 'Current pushed',
      label: 'Current pushed',
      hideable: true,
      sortable: true,
      className: 'whitespace-nowrap text-muted-foreground',
      render: (template) => <DateCell value={template.selectedVersion?.createdAt} />,
    },
    {
      key: 'description',
      header: 'Description',
      label: 'Description',
      hideable: true,
      className: 'max-w-[360px] truncate text-muted-foreground',
      render: (template) => template.description || EMPTY_VALUE,
    },
    {
      key: 'latestVersion',
      header: 'Latest version',
      label: 'Latest version',
      hideable: true,
      defaultHidden: true,
      sortable: true,
      className: 'font-mono text-xs',
      render: (template) =>
        template.latestVersion ? getDisplayVersion(template.latestVersion) : EMPTY_VALUE,
    },
    {
      key: 'latestVersionPushedAt',
      header: 'Latest pushed',
      label: 'Latest pushed',
      hideable: true,
      defaultHidden: true,
      sortable: true,
      className: 'whitespace-nowrap text-muted-foreground',
      render: (template) => <DateCell value={template.latestVersion?.createdAt} />,
    },
    {
      key: 'hasCredentials',
      header: 'Credentials',
      label: 'Credentials',
      hideable: true,
      defaultHidden: true,
      sortable: true,
      render: (template) => (
        <StatusBadge
          status={template.hasCredentials ? 'credentials_provisioned' : 'credentials_missing'}
        />
      ),
    },
    {
      key: 'repositoryUri',
      header: 'Repository URI',
      label: 'Repository URI',
      hideable: true,
      defaultHidden: true,
      className: 'max-w-[280px] truncate font-mono text-xs text-muted-foreground',
      render: (template) => template.ecr.repositoryUri || EMPTY_VALUE,
    },
    {
      key: 'currentVersionDigest',
      header: 'Digest',
      label: 'Digest',
      hideable: true,
      defaultHidden: true,
      className: 'max-w-[280px] truncate font-mono text-xs text-muted-foreground',
      render: (template) => template.selectedVersion?.digest || EMPTY_VALUE,
    },
    {
      key: 'supportedSensorTypes',
      header: 'Sensor types',
      label: 'Sensor types',
      hideable: true,
      defaultHidden: true,
      className: 'max-w-[240px] truncate text-muted-foreground',
      render: (template) => {
        return formatSensorTypes(template.selectedVersion?.supportedSensorTypes);
      },
    },
    {
      key: 'createdAt',
      header: 'Created',
      label: 'Created',
      hideable: true,
      defaultHidden: true,
      sortable: true,
      className: 'whitespace-nowrap text-muted-foreground',
      render: (template) => <DateCell value={template.createdAt} />,
    },
    {
      key: 'updatedAt',
      header: 'Updated',
      label: 'Updated',
      hideable: true,
      defaultHidden: true,
      sortable: true,
      className: 'whitespace-nowrap text-muted-foreground',
      render: (template) => <DateCell value={template.updatedAt} />,
    },
  ];

  const columnVisibility = useColumnVisibility<TemplateProcessor>({
    tableId: TABLE_ID,
    columns,
    defaultSort: DEFAULT_SORT,
  });

  /**
   * The COMPLETE array: GET /template-processors takes no list parameters, so
   * it is read once per organization into `allTemplates`, which the filter
   * counts and the write handlers above also use.
   */
  const templatesResource = useResource(
    async (signal) => {
      const data = await apiService.getTemplateProcessors(orgId as string, accessToken as string, signal);
      return data.filter((template) => template.organizationId === orgId);
    },
    { enabled: Boolean(orgId && accessToken), resetKey: orgId, deps: [accessToken] },
  );
  useEffect(() => {
    if (templatesResource.data) setAllTemplates(templatesResource.data);
  }, [templatesResource.data]);

  /*
   * A memory-source list over that array. The status filter is one of its
   * filters (URL key `review`, as on All Templates): a change re-queries the
   * rows in memory and keeps the search (ruling 3). Writes pin or patch rows
   * through `listRef`; nothing reloads.
   */
  const templatesLoaded = templatesResource.status === 'success' || templatesResource.status === 'refreshing';
  const templatesQuery = useInfiniteList<TemplateProcessor, TemplateSortKey>({
    source: 'memory',
    load: () => allTemplates,
    sourceKey: allTemplates,
    memory: {
      ...TEMPLATE_LIST_CONFIG,
      filter: (template, filters) => matchesTemplateFilter(template, filters.review as TemplateFilter),
    },
    rowKey: (template) => template.id,
    enabled: Boolean(orgId && accessToken) && templatesLoaded,
    resetKey: orgId,
    initialSort: toTemplateSort(columnVisibility.initialSort),
    sortColumns: SORTABLE_KEYS,
    filters: { review: { default: 'all', allowed: TEMPLATE_FILTER_OPTIONS.map((option) => option.value) } },
    urlState: true,
    errorContext: "Couldn't load processor templates",
  });
  listRef.current = templatesQuery;
  const templateFilter = templatesQuery.filters.review as TemplateFilter;

  /**
   * How many templates are waiting on a reviewer — the ONLY count shown.
   *
   * All three used to carry one, and they read as arithmetic that does not
   * hold: the filters are not a partition. A template with no version pushed
   * yet is neither in review nor live, and one that is live while a newer
   * version waits is BOTH. Against real data that surfaced as "All 14, In
   * review 0, Live 10" — correct, and impossible to reconcile by looking at it.
   * A single count on the one filter that is a call to action says something
   * true and invites no subtraction.
   */
  const pendingCount = useMemo(
    () => allTemplates.filter((t) => matchesTemplateFilter(t, 'pending')).length,
    [allTemplates],
  );


  const sortGuard = {
    activeSort: templatesQuery.sort,
    onSortChange: templatesQuery.onSortChange,
  };

  /**
   * Creates a new template using create card values.
   */
  // A status filter that comes back empty says so and offers a way out; it
  // never reads as "no templates yet", and never offers Create (ruling 16).
  const emptyState = (
    <ListEmptyState
      icon={Package}
      noun="templates"
      search={templatesQuery.search}
      onClearSearch={templatesQuery.clearSearch}
      filterActive={templatesQuery.hasActiveFilters}
      onClearFilters={templatesQuery.clearFilters}
      filtered={
        templateFilter === 'pending'
          ? {
            icon: Check,
            title: 'Nothing waiting for review',
            description: 'Versions you submit appear here until an administrator reviews them.',
            // An empty review queue is the answer, not a dead end.
            action: null,
          }
          : templateFilter === 'draft'
            ? {
              title: 'No draft templates',
              description: 'Templates appear here when they have no live version and nothing in review.',
            }
            : {
              title: 'No templates are live',
              description: 'A template goes live once an administrator approves one of its versions.',
            }
      }
      firstRun={{
        title: 'No processor templates yet',
        description:
          'A processor template is a versioned Docker image that runs against your event streams. Create one, push image versions to its registry using its publisher credential, and submit each version for review. A version runs once an administrator approves it.',
        action: mayWriteTemplates ? (
          <Button onClick={() => openWizard(null)}>
            <Plus aria-hidden="true" />
            Create template
          </Button>
        ) : undefined,
      }}
    />
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div className="shrink-0">
        <PageHeader
          title={PAGE_TITLE}
          description={PAGE_DESCRIPTION}
          actions={
            // One creation path: the wizard creates the template itself when
            // opened with a null template. One flag for this and the
            // empty-state Create (DESIGN_SYSTEM §5 Permissions). The filter,
            // search and column picker live on the table (§6).
            selectedOrganization && accessToken && mayWriteTemplates ? (
              <Button onClick={() => openWizard(null)}>
                <Plus aria-hidden="true" />
                Create template
              </Button>
            ) : undefined
          }
        />
      </div>

        {!selectedOrganization && !orgLoading ? (
          <EmptyState
            icon={Package}
            title="No organization selected"
            description="Select an organization from the switcher to view its processor templates."
          />
        ) : !accessToken ? (
          <EmptyState
            icon={Package}
            title="Authentication required"
            description="Please sign in again to manage processor templates."
          />
        ) : (
          <DataTable
            {...listTableProps(templatesQuery)}
            listState={orgLoading ? 'initial' : templatesQuery.status}
            columns={columnVisibility.columns}
            rowKey={(template) => template.id}
            itemNoun="templates"
            listLabel="Processor templates"
            searchable
            filters={
              <ListFilter<TemplateFilter>
                label="Template status"
                value={templateFilter}
                onValueChange={(value) => templatesQuery.setFilter('review', value)}
                options={TEMPLATE_FILTER_OPTIONS}
                attention={{ value: 'pending', count: pendingCount, label: 'in review' }}
              />
            }
            search={
              <SearchInput
                value={templatesQuery.searchInput}
                onChange={templatesQuery.setSearchInput}
                noun="templates"
                placeholder="Search by name, slug, or description…"
                shortcut
              />
            }
            columnPicker={<ColumnPicker columns={columns} state={columnVisibility} sortGuard={sortGuard} />}
            errorState={
              templatesResource.status === 'error' || hasListError(templatesQuery) ? (
                <ErrorState
                  title="Couldn't load processor templates"
                  error={templatesResource.error ?? templatesQuery.error}
                  onRetry={() => {
                    if (templatesResource.status === 'error') void templatesResource.refetch();
                    else templatesQuery.reset();
                  }}
                  isRetrying={templatesResource.status === 'loading' || isListRetrying(templatesQuery)}
                  action={templatesQuery.search ? (
                    <Button variant="outline" size="sm" onClick={templatesQuery.clearSearch}>Clear search</Button>
                  ) : undefined}
                />
              ) : undefined
            }
            emptyState={emptyState}
            onOpenRow={openWorkbench}
            openLabel={(template) => template.name}
          />
        )}

        {selectedOrganizationId && accessToken ? (
          <PublishTemplateWizard
            open={isWizardOpen}
            onOpenChange={(open) => {
              // No reload on close: every wizard write already came back
              // through onTemplateChanged and pinned or patched its row.
              setIsWizardOpen(open);
            }}
            template={wizardTemplate}
            organizationId={selectedOrganizationId}
            accessToken={accessToken}
            onTemplateChanged={handleWizardTemplateChanged}
            mayReadCredentials={mayReadCredentials}
            mayWriteLenses={mayWriteLenses}
            mayMakeLensPublic={mayMakeLensPublic}
            mode={wizardMode}
          />
        ) : null}

        {/*
          The workbench. Rendered BEFORE the wizard in source order is not what
          decides the stacking — Radix portals each dialog as it opens, so the
          wizard, opened second, lands on top and takes the escape key. That is
          the intended layering: the task in front, its destination behind it.
        */}
        {selectedOrganizationId && accessToken ? (
          <TemplateWorkbenchDialog
            open={isWorkbenchOpen}
            onOpenChange={(open) => {
              setIsWorkbenchOpen(open);
              if (!open) setWorkbenchTarget(null);
            }}
            template={workbenchTarget}
            organizationId={selectedOrganizationId}
            accessToken={accessToken}
            mayWriteTemplates={mayWriteTemplates}
            mayReadVersions={mayReadVersions}
            mayWriteVersions={mayWriteVersions}
            mayReadCredentials={mayReadCredentials}
            onTemplateChanged={handleTemplateChanged}
            onNewVersion={openWizard}
            versionsToken={versionsToken}
          />
        ) : null}
    </div>
  );
};

export default MyProcessorTemplates;
